import { useCallback, useMemo } from 'react';

import { SupportedCurrency } from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { isCryptocurrency } from '#src/lib/cryptocurrency.ts';

import { AcceptedCurrenciesValue } from './useAcceptedCurrencies.ts';
import { useCurrentUserAcceptedCurrencies } from './useCurrentUserAcceptedCurrencies.ts';
import { useCurrentUserCurrencies } from './useCurrentUserCurrencies.tsx';
import { LIFECYCLE, useLifecycle } from './useLifecycle.ts';

export const useReferenceCurrencyForSale = () => {
  const {
    walletPreferences: { onlyShowFiatCurrency, enabledWallets },
  } = useCurrentUserContext();
  const { fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();
  const { acceptedCurrencies } = useCurrentUserAcceptedCurrencies();
  const {
    update: storeReferenceCurrencyForSale,
    loading,
    lifecycle,
  } = useLifecycle();

  const update = useCallback(
    (value: SupportedCurrency) => {
      storeReferenceCurrencyForSale(LIFECYCLE.referenceCurrencyForSale, value);
    },
    [storeReferenceCurrencyForSale]
  );

  const lifecycleReferenceCurrencyForSale = lifecycle?.referenceCurrencyForSale;

  const referenceCurrencyForSale = useMemo(() => {
    if (!enabledWallets) return SupportedCurrency.WEI;
    if (onlyShowFiatCurrency) return fiatCurrency;
    switch (acceptedCurrencies) {
      case AcceptedCurrenciesValue.FIAT:
        return fiatCurrency;
      case AcceptedCurrenciesValue.CRYPTO:
        return cryptocurrency;
      case AcceptedCurrenciesValue.BOTH:
        return isCryptocurrency(lifecycleReferenceCurrencyForSale)
          ? cryptocurrency
          : fiatCurrency;
      default:
        return fiatCurrency;
    }
  }, [
    acceptedCurrencies,
    cryptocurrency,
    enabledWallets,
    fiatCurrency,
    lifecycleReferenceCurrencyForSale,
    onlyShowFiatCurrency,
  ]);

  return {
    update,
    loading,
    referenceCurrencyForSale,
  };
};
